/*
 * File: Collection.tsx
 * Project: autobuy-back-office
 * File Created: Friday, 4th August 2023 01:03:31
 * Author: Simon (s.gourlet@autobuy.fr)
 * -----
 * Last Modified: Monday, 21st August 2023 02:37:13
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright 2022 - 2023 SAS Olivier Dubois, SAS Olivier Dubois
 */


import { useEffect, useState } from "react";
import DragGalery from "Content/DragGalery/DragGalery";
import Styles from "../Visuals.module.css";
import { CollectionProps, VisualsType } from "Type/visuals.type";
import CollectionService from "Services/Visuals/Collection/CollectionService";
import VisualService from "Services/Visuals/VisualService";
import { ModalConfirmRequest } from "Content/Modals/ModalConfirmRequest/ModalConfirmRequest";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import translator from "MultiLanguage/TranslationWrapper";
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingScreen from "Content/LoadingScreen/LoadingScreen";

function CollectionVisuals(props: CollectionProps & { onCollectionDelete: () => void }) {
    const [collectionID, setCollectionID] = useState<string>();
    const [visualList, setVisualList] = useState<VisualsType[]>([]);
    const [boxes, setBoxes] = useState<{id: string, order: number, Name: string, Url: string}[]>([]);
    const [requestFunc, setRequestFunc] = useState<Function>(() => () => {});
    const [showModalVisualConfirm, setShowModalVisualConfirm] = useState<boolean>(false);
    const [showModalCollectionConfirm, setShowModalCollectionConfirm] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const [spinner, setSpinner] = useState(false);

    useEffect(() => {
        CollectionService.getCollectionByName(props.name)
            .then((val) => {
                setCollectionID(val.id);
                setVisualList(val.Visuals);
            })
            .catch((e) => {
                console.error(`Unable to load collection: ${props.name}`, e);
            });
    }, [props.name]);

    useEffect(() => {
        setBoxes(visualList.map((val, index) => {
            if (val.Picture)
                return {id: val.id, order: index, Name: val.Picture.Name, Url: val.Picture.URI};
            else if (val.Video)
                return {id: val.id, order: index, Name: val.Video.Name, Url: val.Video.URI};
            return {id: "", order: index, Name: "", Url: ""};
        }));
    }, [visualList]);

    const handleDeleteCollection = () => {
        if (!collectionID)
            return;
        setSpinner(true);
        CollectionService.deleteCollection(collectionID).then(() => {
            setIsOkay(true);
        }).catch(() => {
            setIsOkay(false);
        }).finally(() => {
            setOpenModal(true);
            setShowModalCollectionConfirm(false);
            setSpinner(false);
        });
    }

    const handleDeleteVisual = (id: string) => {
        if (!id)
            return;
        setSpinner(true);
        VisualService.deleteVisual(id).then(() => {
            const tmp = visualList.filter(x => x.id !== id);
            setVisualList(tmp);
            setIsOkay(true);
        }).catch(() => {
            setIsOkay(false);
        }).finally(() => {
            setOpenModal(true);
            setShowModalVisualConfirm(false);
            setSpinner(false);
        });
    };

    const handleToggleDeleteVisualModal = () => {
        setShowModalVisualConfirm(false);
    };

    const handleToggleDeleteCollectionModal = () => {
        setShowModalCollectionConfirm(false);
    };

    const openModalVisualRequest = (id: string) => {
        setRequestFunc(() => () => handleDeleteVisual(id));
        setShowModalVisualConfirm(true);
    };

    const openModalCollectionRequest = () => {
        setRequestFunc(() => () => handleDeleteCollection());
        setShowModalCollectionConfirm(true);
    };

    const handleModalClose = () => {
        setOpenModal(false);
        setTimeout(() => { props.onCollectionDelete()}, 300);
    };

    return (
        <>
            <div className={`${Styles.titleSection}`}>
                <DeleteIcon className={`${Styles.deleteButton}`} color={"error"} onClick={openModalCollectionRequest} />
                <h3 className="w-25 text-decoration-underline">{props.name}</h3>
            </div>
            <div className={`${Styles.picHolder} ms-2`}>
                <DragGalery items={boxes} func={setBoxes} editable onDelete={openModalVisualRequest} />
            </div>
            {
                spinner ?
                    <div className={`${Styles.spinnerHolder}`}>
                        <LoadingScreen />
                    </div>
                    :null
            }
            <ModalConfirmRequest show={showModalVisualConfirm} onClose={handleToggleDeleteVisualModal} closeModal={handleToggleDeleteVisualModal} request={requestFunc} title={translator.decode("visuals.modal.deleteVisual")} />
            <ModalConfirmRequest show={showModalCollectionConfirm} onClose={handleToggleDeleteCollectionModal} closeModal={handleToggleDeleteCollectionModal} request={requestFunc} title={translator.decode("collections.modal.deleteCollection")} />
            <ModalConfirmation show={openModal} onClose={handleModalClose} closeModal={handleModalClose} isOkay={isOkay} />
        </>
    );
}

export default CollectionVisuals
